import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Baby, TrendingUp } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  projectIntergenerationalDebt,
  mortgageOverActiveYears,
} from "@/lib/simulator";
import { fmtMoney, fmtDays, fmtYears } from "@/lib/chart-utils";

function Stat({ label, value, color, testId }) {
  return (
    <div
      className="rounded-md border border-border bg-card p-3"
      data-testid={testId}
    >
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div
        className="mt-1 font-mono text-xl font-bold"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

export default function IntergenerationalDebtPanel({ analysis }) {
  const results = analysis?.results || {};
  const debt = results.debt || {};
  const national = results.national || {};
  const summary = results.summary || {};
  const currency = summary.currency || "UM";
  const autoPublicDebt =
    debt.dette_publique_totale ||
    summary.total_debt_aggregated ||
    0;
  const autoPopulationActive = debt.population_active || 0;
  const autoSalaireJournalier =
    debt.salaire_journalier_moyen ||
    national.salaire_journalier_moyen ||
    results.profiles?.[0]?.individual?.salaire_journalier ||
    0;

  // Manual fallbacks — active when backend has no debt data.
  // Users can ALSO edit the populated values to stress-test scenarios.
  const [manualDebt, setManualDebt] = useState(autoPublicDebt || 0);
  const [manualSalaire, setManualSalaire] = useState(autoSalaireJournalier || 0);

  const publicDebt = Number(manualDebt) || 0;
  const salaireJournalierMoyen = Number(manualSalaire) || 0;

  // Editable inputs with sensible defaults
  const [populationTotal, setPopulationTotal] = useState(
    Math.max((autoPopulationActive || 0) * 3, 1_000_000)
  );
  const [growthRate, setGrowthRate] = useState(5); // % per year
  const [activeYears, setActiveYears] = useState(40);

  const birthYear = new Date().getFullYear();

  const projection = useMemo(
    () =>
      projectIntergenerationalDebt({
        publicDebt,
        populationTotal,
        salaireJournalierMoyen,
        debtGrowthRateAnnual: growthRate / 100,
        horizons: [10, 20, 30],
      }),
    [publicDebt, populationTotal, salaireJournalierMoyen, growthRate]
  );

  const timeline = useMemo(
    () =>
      mortgageOverActiveYears({
        publicDebt,
        populationTotal,
        salaireJournalierMoyen,
        debtGrowthRateAnnual: growthRate / 100,
        activeYears,
      }).map((p) => ({
        ...p,
        year_label: birthYear + p.year_ahead,
      })),
    [
      publicDebt,
      populationTotal,
      salaireJournalierMoyen,
      growthRate,
      activeYears,
      birthYear,
    ]
  );

  const perPerson = projection?.perPerson || {};
  const mortgageAt40 = timeline[timeline.length - 1]?.days_of_life || 0;
  const hasData = publicDebt > 0 && salaireJournalierMoyen > 0;

  return (
    <div className="space-y-5" data-testid="intergenerational-debt-panel">
      {/* Missing data banner (soft) */}
      {!autoPublicDebt && (
        <Card
          className="border-amber-400/40 bg-amber-50/70 dark:border-amber-400/30 dark:bg-amber-950/30"
          data-testid="intergen-manual-banner"
        >
          <CardContent className="flex items-start gap-3 p-4 text-sm">
            <TrendingUp className="mt-0.5 h-5 w-5 text-amber-700 dark:text-amber-300" />
            <div>
              <div className="text-xs uppercase tracking-widest text-amber-800/80 dark:text-amber-200/80">
                Mode simulation manuelle
              </div>
              <p className="mt-1">
                Aucune donnée de dette publique n'a été extraite de votre
                analyse. Saisissez vous-même les paramètres ci-dessous pour
                projeter l'hypothèque vitaliste intergénérationnelle.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual core inputs (always editable, prepopulated when data exists) */}
      <Card data-testid="intergen-core-inputs-card">
        <CardHeader>
          <CardTitle className="text-base">Paramètres de la dette</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label
              htmlFor="manual-debt"
              className="text-xs uppercase tracking-widest text-muted-foreground"
            >
              Dette publique totale ({currency})
            </Label>
            <Input
              id="manual-debt"
              type="number"
              min={0}
              step={1_000_000}
              value={manualDebt}
              onChange={(e) => setManualDebt(Number(e.target.value) || 0)}
              data-testid="intergen-manual-debt"
            />
          </div>
          <div className="space-y-1.5">
            <Label
              htmlFor="manual-salaire"
              className="text-xs uppercase tracking-widest text-muted-foreground"
            >
              Salaire journalier moyen ({currency})
            </Label>
            <Input
              id="manual-salaire"
              type="number"
              min={0}
              step={100}
              value={manualSalaire}
              onChange={(e) => setManualSalaire(Number(e.target.value) || 0)}
              data-testid="intergen-manual-salaire"
            />
          </div>
        </CardContent>
      </Card>

      {!hasData ? (
        <Card
          className="border-dashed"
          data-testid="intergen-no-data"
        >
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Saisissez une dette totale et un salaire journalier moyen non
            nuls pour lancer la projection vitaliste.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Headline */}
          <Card
            className="border-rose-400/40 bg-rose-50/70 dark:border-rose-400/30 dark:bg-rose-950/40"
            data-testid="intergen-headline-card"
          >
            <CardContent className="flex items-start gap-3 p-4">
              <Baby className="mt-0.5 h-6 w-6 text-rose-700 dark:text-rose-300" />
              <div className="text-sm">
                <div className="text-xs uppercase tracking-widest text-rose-800/80 dark:text-rose-200/80">
                  Constat intergénérationnel
                </div>
                <p
                  className="mt-1 text-base font-semibold leading-relaxed"
                  data-testid="intergen-headline"
                >
                  Un enfant né en <strong>{birthYear}</strong> hérite déjà de{" "}
                  <strong className="text-rose-700 dark:text-rose-300">
                    {fmtDays(perPerson.days_of_life)}
                  </strong>{" "}
                  hypothéqués par la dette publique,{" "}
                  <em>sans avoir pu consentir</em>. Au rythme actuel de
                  croissance de la dette ({growthRate}%/an), ce chiffre
                  atteindra{" "}
                  <strong className="text-rose-700 dark:text-rose-300">
                    {fmtDays(mortgageAt40)}
                  </strong>{" "}
                  à ses {activeYears} ans.
                </p>
              </div>
            </CardContent>
          </Card>

      {/* Inputs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Paramètres de projection</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label
              htmlFor="pop-total"
              className="text-xs uppercase tracking-widest text-muted-foreground"
            >
              Population totale (actifs + inactifs + enfants)
            </Label>
            <Input
              id="pop-total"
              type="number"
              min={1}
              step={10000}
              value={populationTotal}
              onChange={(e) =>
                setPopulationTotal(Math.max(1, Number(e.target.value) || 1))
              }
              data-testid="intergen-pop-total"
            />
            <div className="text-[10px] text-muted-foreground">
              Défaut : population active × 3.
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
              Croissance annuelle de la dette :{" "}
              <span className="font-mono font-bold text-brand-blue dark:text-brand-gold">
                {growthRate.toFixed(1)}%
              </span>
            </Label>
            <Slider
              min={0}
              max={20}
              step={0.5}
              value={[growthRate]}
              onValueChange={(v) => setGrowthRate(Number(v[0]))}
              data-testid="intergen-growth-slider"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
              Vie active projetée :{" "}
              <span className="font-mono font-bold text-brand-blue dark:text-brand-gold">
                {activeYears} ans
              </span>
            </Label>
            <Slider
              min={10}
              max={60}
              step={1}
              value={[activeYears]}
              onValueChange={(v) => setActiveYears(Number(v[0]))}
              data-testid="intergen-active-years-slider"
            />
          </div>
        </CardContent>
      </Card>

      {/* Per-person KPIs */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat
          label="Dette par personne (UM)"
          value={fmtMoney(perPerson.debt_um, currency)}
          color="#1A3C5E"
          testId="intergen-per-person-um"
        />
        <Stat
          label="Jours de vie future hypothéqués"
          value={fmtDays(perPerson.days_of_life)}
          color="#C0392B"
          testId="intergen-per-person-days"
        />
        <Stat
          label="Années de vie hypothéquées"
          value={fmtYears(perPerson.years_mortgaged)}
          color="#C9A84C"
          testId="intergen-per-person-years"
        />
      </div>

      {/* Horizon projection */}
      <Card data-testid="intergen-horizons-card">
        <CardHeader>
          <CardTitle className="text-base">
            Projection à 10 / 20 / 30 ans (hypothèse constante)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/30">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="p-3 font-semibold">Horizon</th>
                  <th className="p-3 font-semibold">Dette totale projetée</th>
                  <th className="p-3 font-semibold">Jours/personne</th>
                  <th className="p-3 font-semibold">Années/personne</th>
                </tr>
              </thead>
              <tbody>
                {projection?.projection?.map((row) => (
                  <tr
                    key={row.year_ahead}
                    className="border-b border-border last:border-0"
                    data-testid={`intergen-horizon-row-${row.year_ahead}`}
                  >
                    <td className="p-3 font-semibold">
                      +{row.year_ahead} ans ({birthYear + row.year_ahead})
                    </td>
                    <td className="p-3 font-mono">
                      {fmtMoney(row.debt_um, currency)}
                    </td>
                    <td className="p-3 font-mono text-rose-700 dark:text-rose-300">
                      {fmtDays(row.days_of_life)}
                    </td>
                    <td className="p-3 font-mono">
                      {fmtYears(row.years_mortgaged)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      <Card data-testid="intergen-chart-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-5 w-5 text-rose-600" />
            Évolution de l'hypothèque vitaliste sur la vie active
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <LineChart
                data={timeline}
                margin={{ top: 12, right: 20, left: 8, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.15} />
                <XAxis
                  dataKey="year_label"
                  tick={{ fontSize: 11 }}
                  label={{
                    value: "Année",
                    position: "insideBottomRight",
                    offset: -2,
                    fontSize: 11,
                  }}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  label={{
                    value: "Jours hypothéqués / personne",
                    angle: -90,
                    position: "insideLeft",
                    fontSize: 11,
                    offset: 10,
                  }}
                />
                <Tooltip
                  formatter={(v) => [
                    `${Number(v).toLocaleString("fr-FR")} jours`,
                    "Hypothèque",
                  ]}
                  labelFormatter={(l) => `Année ${l}`}
                />
                <ReferenceLine
                  x={birthYear}
                  stroke="#1A3C5E"
                  strokeDasharray="4 2"
                  label={{ value: "Naissance", position: "top", fontSize: 10 }}
                />
                <Line
                  type="monotone"
                  dataKey="days_of_life"
                  stroke="#C0392B"
                  strokeWidth={2.2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
        </>
      )}
    </div>
  );
}
